import { useMemo } from 'react';
import { Check, Clock, FileText, Lock, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { DealStage, PIPELINE_STAGES } from '@/hooks/useDealPipeline';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface DealPipelineViewProps {
  currentStage: DealStage;
  stageHistory?: { stage: DealStage; timestamp: string }[];
  onStageClick?: (stage: DealStage) => void;
  compact?: boolean;
}

export function DealPipelineView({ 
  currentStage, 
  stageHistory = [], 
  onStageClick,
  compact = false 
}: DealPipelineViewProps) {
  const currentIndex = useMemo(() => 
    PIPELINE_STAGES.findIndex(s => s.stage === currentStage), 
    [currentStage]
  );

  const getStageStatus = (stage: DealStage, index: number) => {
    if (index < currentIndex) return 'completed';
    if (index === currentIndex) return 'current';
    return 'pending';
  };

  const getStageIcon = (stage: DealStage, status: string) => {
    const stageConfig = PIPELINE_STAGES.find(s => s.stage === stage);
    
    if (status === 'completed') {
      return <Check className="h-4 w-4" />;
    }
    
    if (stageConfig?.requiresDocument) {
      return <FileText className="h-4 w-4" />;
    }
    
    if (status === 'current') {
      return <Clock className="h-4 w-4" />;
    }
    
    return <Lock className="h-4 w-4" />;
  };

  const getTimestamp = (stage: DealStage) => {
    const entry = stageHistory.find(h => h.stage === stage);
    if (!entry) return null;
    return new Date(entry.timestamp).toLocaleDateString();
  };

  if (compact) {
    return (
      <div className="flex items-center gap-1">
        {PIPELINE_STAGES.map((stageConfig, index) => {
          const status = getStageStatus(stageConfig.stage, index);
          return (
            <TooltipProvider key={stageConfig.stage}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className={cn(
                      'w-2 h-2 rounded-full transition-colors',
                      status === 'completed' && 'bg-green-500',
                      status === 'current' && 'bg-primary animate-pulse',
                      status === 'pending' && 'bg-muted'
                    )}
                  />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="font-medium">{stageConfig.label}</p>
                  {getTimestamp(stageConfig.stage) && (
                    <p className="text-xs text-muted-foreground">
                      {getTimestamp(stageConfig.stage)}
                    </p>
                  )}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        })}
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="relative">
        {/* Progress line */}
        <div className="absolute top-5 left-0 right-0 h-0.5 bg-muted" />
        <div 
          className="absolute top-5 left-0 h-0.5 bg-primary transition-all duration-500"
          style={{ width: `${(currentIndex / (PIPELINE_STAGES.length - 1)) * 100}%` }}
        />

        {/* Stages */}
        <div className="relative flex justify-between">
          {PIPELINE_STAGES.map((stageConfig, index) => {
            const status = getStageStatus(stageConfig.stage, index);
            const timestamp = getTimestamp(stageConfig.stage);

            return (
              <TooltipProvider key={stageConfig.stage}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => onStageClick?.(stageConfig.stage)}
                      disabled={status === 'pending'}
                      className={cn(
                        'flex flex-col items-center gap-2 group transition-opacity',
                        status === 'pending' && 'opacity-50',
                        onStageClick && status !== 'pending' && 'cursor-pointer hover:opacity-80'
                      )}
                    >
                      <div
                        className={cn(
                          'w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all',
                          status === 'completed' && 'bg-green-500 border-green-500 text-white',
                          status === 'current' && 'bg-primary border-primary text-primary-foreground',
                          status === 'pending' && 'bg-background border-muted text-muted-foreground'
                        )}
                      >
                        {getStageIcon(stageConfig.stage, status)}
                      </div>
                      <span className={cn(
                        'text-xs font-medium text-center max-w-[80px]',
                        status === 'current' && 'text-primary',
                        status === 'completed' && 'text-green-600',
                        status === 'pending' && 'text-muted-foreground'
                      )}>
                        {stageConfig.label}
                      </span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="space-y-1">
                      <p className="font-medium">{stageConfig.label}</p>
                      {stageConfig.requiresDocument && (
                        <p className="text-xs flex items-center gap-1">
                          <FileText className="h-3 w-3" />
                          Requires {stageConfig.requiresDocument.toUpperCase()}
                        </p>
                      )}
                      {timestamp && (
                        <p className="text-xs text-muted-foreground">
                          Completed: {timestamp}
                        </p>
                      )}
                      {status === 'current' && (
                        <p className="text-xs text-primary flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          Current stage
                        </p>
                      )}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          })}
        </div>
      </div>
    </div>
  );
}
